import axios from 'axios';
import userSessionHandler from './userSessionHandler';

// In development, use empty baseURL to leverage the proxy
// In production, use the full API URL
const isDev = process.env.NODE_ENV === 'development';
const BASE_URL = isDev ? '' : process.env.VUE_APP_API_URL;

// Log environment variables at startup
console.log('============================================================');
console.log('FRONTEND ENVIRONMENT VARIABLES CONFIGURATION');
console.log('============================================================');
console.log('  VUE_APP_API_URL:', process.env.VUE_APP_API_URL || '(not set)');
console.log('  NODE_ENV:', process.env.NODE_ENV || '(not set)');
console.log('  BASE_URL (effective):', BASE_URL || '(using proxy)');
console.log('  VUE_APP_FIREBASE_API_KEY:', process.env.VUE_APP_FIREBASE_API_KEY ? '***SET***' : '(not set)');
console.log('  VUE_APP_FIREBASE_AUTH_DOMAIN:', process.env.VUE_APP_FIREBASE_AUTH_DOMAIN || '(not set)');
console.log('  VUE_APP_FIREBASE_PROJECT_ID:', process.env.VUE_APP_FIREBASE_PROJECT_ID || '(not set)');
console.log('============================================================');

const api = axios.create({baseURL: BASE_URL});

// Add auth token to requests if available
api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Add response interceptor to handle authentication errors
api.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401 && !userSessionHandler.isExternalFeedError(error)) {
            userSessionHandler.handleUserLogout();
        }
        return Promise.reject(error);
    }
);

const apiService = {
    getSources() {
        return api.get('/source/sources')
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    getSource(id) {
        return api.get(`/source/sources/${id}`)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    createSource(datasource) {
        return api.post('/source/sources', datasource)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    createContentSource(name, description, content) {
        const payload = {
            name,
            description,
            content,
            kind: "content",
            connection: {
                provider: "content"
            }
        };
        return api.post('/source/sources/content', payload)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    createFileSource(name, description, file) {
        const formData = new FormData();
        formData.append('name', name);
        formData.append('description', description || '');
        formData.append('file', file);
        
        return api.post('/source/sources/file', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    updateSource(id, sourceData) {
        return api.put(`/source/sources/${id}`, sourceData)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    updateFileSource(id, file) {
        const formData = new FormData();
        formData.append('file', file);
        
        return api.put(`/source/sources/${id}/file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    // Added delete method
    deleteSource(id) {
        return api.delete(`/source/sources/${id}`)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    testSourceConnection(sourceId) {
        return api.post(`/source/sources/${sourceId}/test-connection`)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    parseFeedModelsPaths(auth, datasourceType, datasource) {
        let apiPayload;
        if (datasourceType === "file") {
            apiPayload = new FormData();
            apiPayload.append('datasource_type', datasourceType);
            apiPayload.append('datasource', datasource);
        } else {
            apiPayload = {auth, datasource_type: datasourceType, datasource}
        }

        return api.post('/parsing/feed/models/paths', apiPayload)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    parseFeedModelStructure(auth, modelPath, datasourceType, datasource) {
        let apiPayload;
        if (datasourceType === "file") {
            apiPayload = new FormData();
            apiPayload.append('datasource_type', datasourceType);
            apiPayload.append('datasource', datasource);
            apiPayload.append('model_path', modelPath);
        } else {
            apiPayload = {auth, model_path: modelPath, datasource_type: datasourceType, datasource}
        }

        return api.post('/parsing/feed/model/structure', apiPayload)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    parseFeedModelItems(auth, modelPath, datasourceType, datasource) {
        let apiPayload;
        if (datasourceType === "file") {
            apiPayload = new FormData();
            apiPayload.append('datasource_type', datasourceType);
            apiPayload.append('datasource', datasource);
            apiPayload.append('model_path', modelPath);
        } else {
            apiPayload = {auth, model_path: modelPath, datasource_type: datasourceType, datasource}
        }
        return api.post('/parsing/feed/model/items', apiPayload)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    // Authentication methods (login/register/logout handled by Firebase)
    getCurrentUser() {
        return api.get('/auth/me')
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    updateUserProfile(userData) {
        return api.put('/auth/profile', userData)
            .then(response => response.data)
            .catch(error => this.handleError(error));
    },

    handleError(error) {
        if (error.response) {
            // Request made and server responded
            console.error('Data:', error.response.data);
            console.error('Status:', error.response.status);
            console.error('Headers:', error.response.headers);

            // Check if the response data has an 'errors' property
            if (error.response.data && error.response.data.errors) {
                // Construct a string with all the error messages
                let errorMessage = '\n';
                errorMessage += 'Errors: \n';

                // Concatenate all errors. Assuming errorsList is an array of strings.
                errorMessage += JSON.stringify(error.response.data.errors, null, 2);

                throw new Error(errorMessage);
            } else {
                // If no specific errors are provided, use the status text
                throw new Error(`API Error: ${error.response.statusText}`);
            }
        } else if (error.request) {
            // Request made but no response received
            console.error('Request:', error.request);
            throw new Error('No response from server');
        } else {
            // Something else caused the error
            console.error('Error:', error.message);
            throw new Error(error.message);
        }
    }

};

export default apiService;
